import { AutoComplete, AutoCompleteCompleteEvent } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useContext, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
    appInnerCardStyle,
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

// Type definitions

export interface MedicationEntry {
    name: string;                 // Medication (Prescribed, OTC & Herbal)
    dosageRouteFrequency: string; // Dosage, Route and Frequency
    use: string;                  // Use
    specialInstructions: string;  // Special Instructions (incl. contraindications & side effects)
}

export interface Step05FormValues {
    allergies: string;
    primaryMD: string;
    medications: MedicationEntry[];
}

interface PsychotropicMedicationSuggestion {
    productName: string;
    drugClass: string;
}

export const DEFAULT_MEDICATION: MedicationEntry = {
    name: "", dosageRouteFrequency: "", use: "", specialInstructions: "",
};

export const DEFAULT_STEP05: Step05FormValues = {
    allergies: "",
    primaryMD: "",
    medications: [{ ...DEFAULT_MEDICATION }],
};

// Props

export interface Step05Props {
    sectionData: Partial<Step05FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step05FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

// Component

const Step05CurrentlyPrescribedMedications: React.FC<Step05Props> = ({
    sectionData, residentId, formId, onSave, onNext, onBack, loading,
}) => {
    const { backendAPIStore } = useContext(MobXProviderContext) as any;
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);
    const [medicationSuggestions, setMedicationSuggestions] = useState<Record<number, PsychotropicMedicationSuggestion[]>>({});
    const [generatingMedicationAi, setGeneratingMedicationAi] = useState<Record<number, boolean>>({});

    const defaultValues: Step05FormValues = {
        ...DEFAULT_STEP05,
        ...(sectionData ?? {}),
        allergies: sectionData?.allergies ?? "",
        primaryMD: sectionData?.primaryMD ?? "",
        medications:
            sectionData?.medications && sectionData.medications.length > 0
                ? sectionData.medications.map((m) => ({
                    name: m.name ?? "",
                    dosageRouteFrequency: m.dosageRouteFrequency ?? "",
                    use: m.use ?? "",
                    specialInstructions: m.specialInstructions ?? "",
                }))
                : [{ ...DEFAULT_MEDICATION }],
    };

    const { control, handleSubmit, watch, reset, getValues, setValue } = useForm<Step05FormValues>({ defaultValues });
    const { fields: medFields, append: appendMed, remove: removeMed } = useFieldArray({ control, name: "medications" });

    const stripEmpty = (data: Step05FormValues): Step05FormValues => ({
        ...data,
        medications: data.medications.filter((m) => m.name.trim().length > 0),
    });

    const onSubmit = async (data: Step05FormValues) => {
        await onSave(stripEmpty(data));
    };

    const requestRemoveMedication = (index: number) => {
        confirmDialog({
            header: "Remove Medication",
            message: `Remove medication ${index + 1} from this assessment step?`,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            acceptClassName: "p-button-danger p-button-rounded",
            rejectClassName: "p-button-text p-button-rounded",
            accept: () => removeMed(index),
        });
    };

    const handleSave = async (data: Step05FormValues) => {
        try {
            await onSave(stripEmpty(data));
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Medications saved successfully.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    const handleSaveAndContinue = async (data: Step05FormValues) => {
        try {
            await onSave(stripEmpty(data));
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Medications saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message || "An error occurred while saving.", life: 4000 });
        }
    };

    const searchMedicationSuggestions = async (index: number, event: AutoCompleteCompleteEvent) => {
        const query = String(event.query || "").trim();
        if (query.length < 1) {
            setMedicationSuggestions((current) => ({ ...current, [index]: [] }));
            return;
        }

        try {
            const response = await backendAPIStore.searchPsychotropicMedicationList(query, 8);
            setMedicationSuggestions((current) => ({
                ...current,
                [index]: Array.isArray(response?.items) ? response.items : [],
            }));
        } catch (error) {
            setMedicationSuggestions((current) => ({ ...current, [index]: [] }));
        }
    };

    const renderMedicationSuggestion = (suggestion: PsychotropicMedicationSuggestion) => (
        <div className="flex flex-column">
            <span className="font-medium">{suggestion.productName}</span>
            <small style={{ color: "#6b7280" }}>{suggestion.drugClass}</small>
        </div>
    );

    const generateMedicationBenefitsAndRisks = async (index: number) => {
        const medicationEntry = getValues("medications")?.[index] || DEFAULT_MEDICATION;
        const medicationName = String(medicationEntry?.name || "").trim();
        if (!medicationName || generatingMedicationAi[index]) {
            return;
        }

        setGeneratingMedicationAi((current) => ({ ...current, [index]: true }));
        try {
            const response = await backendAPIStore.formatAiText({
                taskType: "professional-note-cleanup",
                tone: "professional",
                domain: "assessment-medication-benefits-risks",
                outputStyle: "plain_text",
                metadata: {
                    medicationName,
                    currentBenefits: String(medicationEntry?.use || "").trim(),
                    currentRisks: String(medicationEntry?.specialInstructions || "").trim(),
                },
            });
            const generatedText = String(response?.formattedText || "").trim();
            const readGeneratedField = (label: string, nextLabels: string[]) => {
                const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                const escapedNextLabels = nextLabels.map((nextLabel) => nextLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
                const nextFieldPattern = escapedNextLabels.length > 0
                    ? `(?=\\n(?:${escapedNextLabels.join("|")})\\s*:|$)`
                    : "(?=$)";
                const match = generatedText.match(new RegExp(`${escapedLabel}\\s*:\\s*([\\s\\S]*?)${nextFieldPattern}`, "i"));
                return String(match?.[1] || "").trim();
            };
            const generatedBenefits = readGeneratedField("Benefits", ["Risks"]);
            const generatedRisks = readGeneratedField("Risks", []);
            if (generatedBenefits) {
                setValue(`medications.${index}.use`, generatedBenefits, { shouldDirty: true, shouldTouch: true });
            }
            if (generatedRisks) {
                setValue(`medications.${index}.specialInstructions`, generatedRisks, { shouldDirty: true, shouldTouch: true });
            }
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: "AI generation failed",
                detail: error?.message || "Unable to generate medication benefits and risks.",
                life: 4000,
            });
        } finally {
            setGeneratingMedicationAi((current) => ({ ...current, [index]: false }));
        }
    };

    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Currently Prescribed Medications", "Medication summary.", "pi pi-file-edit")}
                            <div className="p-3">
                                <div className="grid mb-3">
                                    <InfoRow label="Allergies" value={values.allergies} />
                                    <InfoRow label="Primary MD" value={values.primaryMD} />
                                </div>
                                {(values.medications ?? []).filter(m => m.name).length > 0 ? (
                                    <div>
                                        {(values.medications ?? []).filter(m => m.name).map((m, i) => (
                                            <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "0.5rem 0.75rem", marginBottom: 8 }}>
                                                <div className="font-medium text-sm">{m.name}</div>
                                                {m.dosageRouteFrequency && <div className="text-xs" style={{ color: "#6b7280" }}>{m.dosageRouteFrequency}</div>}
                                                {m.use && <div className="text-xs" style={{ color: "#6b7280" }}>Use: {m.use}</div>}
                                                {m.specialInstructions && <div className="text-xs" style={{ color: "#6b7280" }}>Instructions: {m.specialInstructions}</div>}
                                            </div>
                                        ))}
                                    </div>
                                ) : <p className="text-sm" style={{ color: "#9ca3af" }}>No medications recorded.</p>}
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader(
                                "Currently Prescribed Medications",
                                "List all current medications including prescribed, OTC, and herbal. This list is only current at the time of assessment. You may contact the Pharmacist or Physician to inquire about contraindications.",
                                "pi pi-file-edit",
                            )}
                            <div className="p-3">
                                {/* Allergies + Primary MD */}
                                <div className="grid mb-3">
                                    <div className="col-12 md:col-6">
                                        <label className="font-medium text-sm block mb-1" style={{ color: "#374151" }}>Allergies</label>
                                        <Controller
                                            name="allergies"
                                            control={control}
                                            render={({ field: f }) => (
                                                <InputText {...f} className="w-full" placeholder="List known allergies" />
                                            )}
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <label className="font-medium text-sm block mb-1" style={{ color: "#374151" }}>Primary MD</label>
                                        <Controller
                                            name="primaryMD"
                                            control={control}
                                            render={({ field: f }) => (
                                                <InputText {...f} className="w-full" placeholder="Primary physician name" />
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Medication list */}
                                <div className="flex flex-column gap-3">
                                    {medFields.map((field, index) => (
                                        <div
                                            key={field.id}
                                            style={{
                                                ...appInnerCardStyle,
                                                padding: "0.7rem 0.85rem",
                                                background: "#fffaf0",
                                                border: "1px solid #fde68a",
                                            }}
                                        >
                                            <div className="flex align-items-center justify-content-between mb-2">
                                                <span style={{
                                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                    width: 22, height: 22, borderRadius: "50%",
                                                    background: "#e2e8f0", color: "#475569",
                                                    fontSize: "0.70rem", fontWeight: 700,
                                                }}>
                                                    {index + 1}
                                                </span>
                                                <Button
                                                    type="button" icon="pi pi-trash"
                                                    rounded text severity="danger"
                                                    aria-label={`Remove medication ${index + 1}`}
                                                    disabled={medFields.length === 1}
                                                    onClick={() => requestRemoveMedication(index)}
                                                    style={{ width: 28, height: 28, color: "#dc2626" }}
                                                />
                                            </div>

                                            {/* Medication name — full width */}
                                            <Controller
                                                name={`medications.${index}.name`}
                                                control={control}
                                                render={({ field: f }) => (
                                                    <AutoComplete
                                                        value={f.value}
                                                        suggestions={medicationSuggestions[index] || []}
                                                        completeMethod={(event) => { void searchMedicationSuggestions(index, event); }}
                                                        field="productName"
                                                        className="w-full mb-2"
                                                        inputClassName="w-full p-inputtext-sm"
                                                        forceSelection={false}
                                                        dropdown={false}
                                                        delay={250}
                                                        placeholder="Medication name (Prescribed, OTC or Herbal)"
                                                        itemTemplate={renderMedicationSuggestion}
                                                        onChange={(event) => {
                                                            const nextValue = typeof event.value === "string"
                                                                ? event.value
                                                                : event.value?.productName || "";
                                                            f.onChange(nextValue);
                                                        }}
                                                    />
                                                )}
                                            />

                                            {/* Dosage + Use — side by side */}
                                            <div
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
                                                    gap: "0.45rem",
                                                    marginBottom: "0.45rem",
                                                }}
                                            >
                                                <Controller
                                                    name={`medications.${index}.dosageRouteFrequency`}
                                                    control={control}
                                                    render={({ field: f }) => (
                                                        <InputText
                                                            {...f}
                                                            placeholder="Dosage, route and frequency"
                                                            className="p-inputtext-sm"
                                                            style={{ width: "100%", minWidth: 0 }}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`medications.${index}.use`}
                                                    control={control}
                                                    render={({ field: f }) => (
                                                        <InputText
                                                            {...f}
                                                            placeholder="Use"
                                                            className="p-inputtext-sm"
                                                            style={{ width: "100%", minWidth: 0 }}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            {/* Special instructions */}
                                            <Controller
                                                name={`medications.${index}.specialInstructions`}
                                                control={control}
                                                render={({ field: f }) => (
                                                    <InputTextarea
                                                        {...f}
                                                        className="w-full"
                                                        rows={1}
                                                        autoResize
                                                        placeholder="Special instructions (including contraindications and common side effects)"
                                                        style={{ minHeight: "44px" }}
                                                    />
                                                )}
                                            />
                                            <div className="flex justify-content-center mt-2">
                                                <Button
                                                    type="button"
                                                    label="Generate with AI"
                                                    icon="pi pi-sparkles"
                                                    severity="warning"
                                                    outlined
                                                    rounded
                                                    loading={!!generatingMedicationAi[index]}
                                                    disabled={!String(values.medications?.[index]?.name || "").trim()}
                                                    onClick={() => { void generateMedicationBenefitsAndRisks(index); }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <div className="flex justify-content-end">
                                        <Button
                                            type="button"
                                            label="Add Medication"
                                            icon="pi pi-plus"
                                            severity="warning"
                                            rounded
                                            onClick={() => appendMed({ ...DEFAULT_MEDICATION })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step05CurrentlyPrescribedMedications;
